import { useEffect, useState } from "react";
import { getMyOrganization } from "../api/services/organization";

export enum EOrganizationState {
  LOADING = "LOADING",
  NO_ORGANIZATION = "NO_ORGANIZATION",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  VERIFIED = "VERIFIED",
}

export interface IOrganizationState {
  state: EOrganizationState;
  code?: string;
  rejectionReason?: string;
  name?: string;
  logoUrl?: string;
  websiteUrl?: string;
  description?: string;
}

/**
 * Resolves the logged-in account's organization state for portal screen routing.
 *
 * Always resolves via GET /api/organizations/my-organization — the DB-backed, real-time
 * verificationStatus. organizationAdminCode/clientAdminClientId JWT claims are NOT a reliable proxy
 * for "verified": auth-service now pushes them as soon as an account is linked to an organization
 * (self-registration time), not only once an admin approves it, so their presence alone no longer
 * distinguishes PENDING from VERIFIED. Authorization to call org-scoped endpoints still comes from
 * those claims (see useOrganizationCode) — this hook only decides which screen to render.
 */
export function useOrganizationState(
  userToken?: string | null
): IOrganizationState {
  const [state, setState] = useState<IOrganizationState>({
    state: EOrganizationState.LOADING,
  });

  useEffect(() => {
    if (!userToken) {
      return;
    }

    let cancelled = false;
    getMyOrganization()
      .then((response) => {
        if (cancelled) return;
        const data = response?.data;
        if (!data?.hasOrganization) {
          setState({ state: EOrganizationState.NO_ORGANIZATION });
          return;
        }
        if (data.verificationStatus === "REJECTED") {
          setState({
            state: EOrganizationState.REJECTED,
            code: data.code,
            rejectionReason: data.rejectionReason,
            name: data.name,
            logoUrl: data.logoUrl,
            websiteUrl: data.websiteUrl,
            description: data.description,
          });
        } else if (data.verificationStatus === "VERIFIED") {
          setState({ state: EOrganizationState.VERIFIED, code: data.code });
        } else {
          // name/logoUrl feed the gate shell's brand slot while the org waits on approval.
          setState({
            state: EOrganizationState.PENDING,
            code: data.code,
            name: data.name,
            logoUrl: data.logoUrl,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ state: EOrganizationState.NO_ORGANIZATION });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userToken]);

  return state;
}
